import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faClock, faTag, faShareNodes, faSpinner, faHeart, faEye, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faXTwitter, faLinkedin, faWhatsapp, faTelegram, faReddit, faPinterest } from '@fortawesome/free-brands-svg-icons';
import './BlogDetail.css';

var API_BASE = process.env.REACT_APP_API_URL      || 'http://localhost:3001/api/v1';
var OG_BASE  = process.env.REACT_APP_OG_BASE_URL  || 'https://blog-backend-8oer.onrender.com';

var fadeUp  = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } } };
var stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
}

function updatePostSEO(post, url) {
  var excerpt = (post.excerpt || '').substring(0, 160);
  document.title = post.title + ' | Jacob Chidi Eugene';
  function setMeta(selector, value) {
    var el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      var match = selector.match(/\[([a-z:]+)="([^"]+)"\]/);
      if (match) { el.setAttribute(match[1], match[2]); }
      document.head.appendChild(el);
    }
    el.setAttribute('content', value);
  }
  setMeta('meta[name="description"]',         excerpt);
  setMeta('meta[property="og:title"]',        post.title);
  setMeta('meta[property="og:description"]',  excerpt);
  setMeta('meta[property="og:url"]',          url);
  setMeta('meta[property="og:type"]',         'article');
  if (post.coverImage) setMeta('meta[property="og:image"]', post.coverImage);
  setMeta('meta[name="twitter:title"]',       post.title);
  setMeta('meta[name="twitter:description"]', excerpt);
}

/* ─── Inline content renderer ────────────────────────────────────
   Converts inside a paragraph:
   - [text](url)        → <a href="url" target="_blank">text</a>
   - `code`             → <code>code</code>
   - **bold**           → <strong>bold</strong>
   - plain text         → text node
──────────────────────────────────────────────────────────────── */
function renderInline(text, key) {
  // Combined regex: matches [text](url), `code`, or **bold**
  var pattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*/g;
  var parts = [];
  var lastIndex = 0;
  var match;
  var idx = 0;

  while ((match = pattern.exec(text)) !== null) {
    // Plain text before this match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      // [text](url) — external affiliate / regular link
      parts.push(
        React.createElement('a', {
          key: key + '-link-' + idx,
          href: match[2],
          target: '_blank',
          rel: 'noopener noreferrer',
          className: 'bd-inline-link',
        }, match[1])
      );
    } else if (match[3]) {
      // `code`
      parts.push(
        React.createElement('code', { key: key + '-code-' + idx, className: 'bd-inline-code' }, match[3])
      );
    } else if (match[4]) {
      // **bold**
      parts.push(
        React.createElement('strong', { key: key + '-bold-' + idx }, match[4])
      );
    }

    lastIndex = match.index + match[0].length;
    idx++;
  }

  // Remaining plain text after last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/* ─── Share Panel ────────────────────────────────────────────── */
var SharePanel = function(props) {
  var url      = props.url;
  var copyUrl  = props.copyUrl || props.url;
  var title    = props.title;
  var excerpt  = props.excerpt || '';

  var copiedState = useState(false); var copied = copiedState[0]; var setCopied = copiedState[1];

  var encoded     = encodeURIComponent(url);
  var encodedText = encodeURIComponent(title);
  var encodedFull = encodeURIComponent(title + ' — ' + excerpt.slice(0, 100));

  var networks = [
    { id: 'twitter',   label: 'X / Twitter', icon: faXTwitter,  color: '#000000', href: 'https://twitter.com/intent/tweet?url=' + encoded + '&text=' + encodedText + '&via=JacobChidiEugene' },
    { id: 'facebook',  label: 'Facebook',    icon: faFacebook,  color: '#1877f2', href: 'https://www.facebook.com/sharer/sharer.php?u=' + encoded },
    { id: 'linkedin',  label: 'LinkedIn',    icon: faLinkedin,  color: '#0a66c2', href: 'https://www.linkedin.com/sharing/share-offsite/?url=' + encoded },
    { id: 'whatsapp',  label: 'WhatsApp',    icon: faWhatsapp,  color: '#25d366', href: 'https://api.whatsapp.com/send?text=' + encodeURIComponent(title + '\n' + url) },
    { id: 'telegram',  label: 'Telegram',    icon: faTelegram,  color: '#229ed9', href: 'https://t.me/share/url?url=' + encoded + '&text=' + encodedText },
    { id: 'reddit',    label: 'Reddit',      icon: faReddit,    color: '#ff4500', href: 'https://reddit.com/submit?url=' + encoded + '&title=' + encodedText },
    { id: 'pinterest', label: 'Pinterest',   icon: faPinterest, color: '#e60023', href: 'https://pinterest.com/pin/create/button/?url=' + encoded + '&description=' + encodedFull },
  ];

  function handleCopy() {
    navigator.clipboard.writeText(copyUrl).then(function() {
      setCopied(true); setTimeout(function() { setCopied(false); }, 2200);
    }).catch(function() {
      var ta = document.createElement('textarea');
      ta.value = copyUrl; document.body.appendChild(ta);
      ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true); setTimeout(function() { setCopied(false); }, 2200);
    });
  }

  function handleNativeShare() {
    if (navigator.share) {
      navigator.share({ title: title, text: excerpt.slice(0, 100), url: copyUrl }).catch(function() {});
    }
  }

  return (
    <div className="bd-share">
      <div className="bd-share__header">
        <FontAwesomeIcon icon={faShareNodes} />
        <span>Share this article</span>
      </div>
      <div className="bd-share__grid">
        {networks.map(function(n) {
          return (
            <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer"
              className="bd-share__btn" style={{ '--share-color': n.color }}
              aria-label={'Share on ' + n.label} title={n.label}>
              <FontAwesomeIcon icon={n.icon} className="bd-share__btn-icon" />
              <span className="bd-share__btn-label">{n.label}</span>
            </a>
          );
        })}
        <button
          className={'bd-share__btn bd-share__btn--copy' + (copied ? ' bd-share__btn--copied' : '')}
          style={{ '--share-color': copied ? '#4ade80' : 'var(--clr-text-muted)' }}
          onClick={handleCopy} aria-label="Copy link" title="Copy link">
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="bd-share__btn-icon" />
          <span className="bd-share__btn-label">{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
        {typeof navigator !== 'undefined' && navigator.share && (
          <button className="bd-share__btn bd-share__btn--native"
            style={{ '--share-color': 'var(--clr-gold)' }}
            onClick={handleNativeShare} aria-label="More share options" title="More options">
            <FontAwesomeIcon icon={faShareNodes} className="bd-share__btn-icon" />
            <span className="bd-share__btn-label">More</span>
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── Comment form ───────────────────────────────────────────── */
var CommentForm = function(props) {
  var postId      = props.postId;
  var onSubmitted = props.onSubmitted;
  var nameState    = useState('');    var name    = nameState[0];    var setName    = nameState[1];
  var emailState   = useState('');    var email   = emailState[0];   var setEmail   = emailState[1];
  var bodyState    = useState('');    var body    = bodyState[0];    var setBody    = bodyState[1];
  var statusState  = useState('');    var status  = statusState[0];  var setStatus  = statusState[1];
  var loadingState = useState(false); var loading = loadingState[0]; var setLoading = loadingState[1];

  var handleSubmit = function(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !body.trim()) { setStatus('error:Please fill in all fields.'); return; }
    setLoading(true); setStatus('');
    fetch(API_BASE + '/comments', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authorName: name, authorEmail: email, body: body, postId: postId }),
    })
      .then(function(r) { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(function() {
        setStatus('success:Thank you! Your comment is awaiting moderation.');
        setName(''); setEmail(''); setBody('');
        if (onSubmitted) onSubmitted();
      })
      .catch(function() { setStatus('error:Something went wrong. Please try again.'); })
      .finally(function() { setLoading(false); });
  };

  return (
    <div className="bd-comment-form">
      <p className="bd-sidebar__heading">Leave a Comment</p>
      {status && (
        <div className={'bd-form-status bd-form-status--' + (status.startsWith('success') ? 'success' : 'error')}>
          {status.replace(/^(success|error):/, '')}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="bd-form-row">
          <input className="bd-form-input" type="text" placeholder="Your name" value={name} onChange={function(e) { setName(e.target.value); }} required />
          <input className="bd-form-input" type="email" placeholder="Your email" value={email} onChange={function(e) { setEmail(e.target.value); }} required />
        </div>
        <textarea className="bd-form-textarea" placeholder="Share your thoughts..." value={body} onChange={function(e) { setBody(e.target.value); }} rows={4} required />
        <button type="submit" className="home-btn home-btn--primary" disabled={loading} style={{ marginTop: 8 }}>
          {loading ? 'Submitting...' : 'Post Comment'}
        </button>
      </form>
    </div>
  );
};

/* ─── Comments list ──────────────────────────────────────────── */
var CommentsList = function(props) {
  var postId  = props.postId;
  var refresh = props.refresh;
  var commentsState = useState([]); var comments = commentsState[0]; var setComments = commentsState[1];

  useEffect(function() {
    if (!postId) return;
    fetch(API_BASE + '/comments/post/' + postId)
      .then(function(r) { return r.json(); })
      .then(setComments)
      .catch(function() {});
  }, [postId, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  if (comments.length === 0) return null;

  return (
    <div className="bd-comments">
      <p className="bd-sidebar__heading">{comments.length} Comment{comments.length !== 1 ? 's' : ''}</p>
      {comments.map(function(c) {
        return (
          <div key={c.id} className="bd-comment">
            <div className="bd-comment__avatar">{c.authorName.charAt(0).toUpperCase()}</div>
            <div className="bd-comment__content">
              <div className="bd-comment__header">
                <span className="bd-comment__author">{c.authorName}</span>
                <span className="bd-comment__date">{formatDate(c.createdAt)}</span>
              </div>
              <p className="bd-comment__body">{c.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────────── */
var BlogDetail = function() {
  var params = useParams();
  var slug   = params.id;

  var postState    = useState(null);  var post    = postState[0];    var setPost    = postState[1];
  var loadingState = useState(true);  var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState   = useState('');    var error   = errorState[0];   var setError   = errorState[1];
  var relatedState = useState([]);    var related = relatedState[0]; var setRelated = relatedState[1];
  var likesState   = useState(0);     var likes   = likesState[0];   var setLikes   = likesState[1];
  var likedState   = useState(false); var liked   = likedState[0];   var setLiked   = likedState[1];
  var refreshState = useState(0);     var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    if (!slug) return;
    setLoading(true); setError(''); setPost(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    fetch(API_BASE + '/posts/' + slug + '/slug')
      .then(function(r) {
        if (!r.ok) throw new Error('Post not found');
        return r.json();
      })
      .then(function(data) {
        setPost(data);
        setLikes(data.likeCount || 0);
        updatePostSEO(data, window.location.href);
        if (data.category) {
          var catParams = new URLSearchParams({ limit: 4, categoryId: data.category.id });
          fetch(API_BASE + '/posts?' + catParams.toString())
            .then(function(r) { return r.json(); })
            .then(function(res) {
              setRelated((res.data || []).filter(function(p) { return p.id !== data.id; }).slice(0, 3));
            })
            .catch(function() {});
        }
      })
      .catch(function(e) { setError(e.message); })
      .finally(function() { setLoading(false); });

    var likedPosts = JSON.parse(localStorage.getItem('jce_liked_posts') || '{}');
    setLiked(Boolean(likedPosts[slug]));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  var handleLike = function() {
    if (liked || !post) return;
    fetch(API_BASE + '/posts/' + post.id + '/like', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setLikes(data.likeCount); setLiked(true);
        var likedPosts = JSON.parse(localStorage.getItem('jce_liked_posts') || '{}');
        likedPosts[slug] = true;
        localStorage.setItem('jce_liked_posts', JSON.stringify(likedPosts));
      })
      .catch(function() {});
  };

  var shareUrl = OG_BASE + '/api/v1/posts/' + (post ? post.slug : '') + '/og';
  var copyUrl  = window.location.href;

  if (loading) return (
    <div className="bd-root">
      <div className="bd-loading"><FontAwesomeIcon icon={faSpinner} spin /><span>Loading article...</span></div>
    </div>
  );

  if (error || !post) return (
    <div className="bd-root">
      <section className="bd-notfound">
        <Container>
          <span className="home-label">404</span>
          <h1 className="bd-notfound__title">Post Not Found</h1>
          <p className="bd-notfound__sub">This article does not exist or has not been published yet.</p>
          <Link to="/blog" className="home-btn home-btn--outline"><FontAwesomeIcon icon={faArrowLeft} /> Back to Blog</Link>
        </Container>
      </section>
    </div>
  );

  return (
    <motion.div className="bd-root" initial="hidden" animate="show" variants={stagger}>

      {/* Hero */}
      <section className="bd-hero" aria-label="Article header">
        <div className="bd-hero__glow" aria-hidden="true" />
        {post.coverImage && (
          <div className="bd-hero__cover"><img src={post.coverImage} alt={post.title} /></div>
        )}
        <Container>
          <motion.div variants={fadeUp}>
            <Link to="/blog" className="bd-back"><FontAwesomeIcon icon={faArrowLeft} /> All Articles</Link>
          </motion.div>
          <motion.div className="bd-hero__meta" variants={fadeUp}>
            {post.category && (
              <span className="blog-card__category" style={{ color: post.category.color || 'var(--clr-gold)' }}>
                <FontAwesomeIcon icon={faTag} /> {post.category.name}
              </span>
            )}
            <span className="blog-card__read-time"><FontAwesomeIcon icon={faClock} /> {post.readTimeMinutes} min read</span>
            <span className="blog-card__date">{formatDate(post.createdAt)}</span>
            <span className="bd-hero__views"><FontAwesomeIcon icon={faEye} /> {post.viewCount} views</span>
          </motion.div>
          <motion.h1 className="bd-hero__title" variants={fadeUp}>{post.title}</motion.h1>
          <motion.p className="bd-hero__excerpt" variants={fadeUp}>{post.excerpt}</motion.p>
          <motion.div className="bd-hero__author" variants={fadeUp}>
            <div className="bd-author-avatar" aria-hidden="true">JCE</div>
            <div>
              <span className="bd-author-name">Jacob Chidi Eugene</span>
              <span className="bd-author-role">Snr Software Engineer</span>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Content */}
      <section className="bd-content" aria-label="Article content">
        <Container>
          <div className="bd-layout">

            <motion.article className="bd-article" variants={stagger} aria-label={post.title}>
              <div className="bd-article__body">
                {post.content.split('\n\n').map(function(block, i) {
                  var text = block.trim();
                  if (!text) return null;

                  // YouTube embed
                  var ytMatch = text.match(/\[youtube:\s*(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\]]*)\]/);
                  if (ytMatch) {
                    var videoId = ytMatch[2];
                    var caption = text.replace(ytMatch[0], '').trim();
                    return (
                      <motion.div key={i} className="bd-embed bd-embed--youtube" variants={fadeUp}>
                        <div className="bd-embed__wrap">
                          <iframe src={'https://www.youtube.com/embed/' + videoId + '?rel=0&modestbranding=1'}
                            title="YouTube video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen />
                        </div>
                        {caption && <p className="bd-embed__caption">{caption}</p>}
                      </motion.div>
                    );
                  }

                  // Image embed
                  var imgMatch = text.match(/\[image:\s*([^\]|]+)(?:\|([^\]]*))?\]/);
                  if (imgMatch) {
                    return (
                      <motion.figure key={i} className="bd-embed bd-embed--image" variants={fadeUp}>
                        <img src={imgMatch[1].trim()} alt={imgMatch[2] ? imgMatch[2].trim() : ''} loading="lazy" />
                        {imgMatch[2] && <figcaption className="bd-embed__caption">{imgMatch[2].trim()}</figcaption>}
                      </motion.figure>
                    );
                  }

                  // Blockquote
                  if (text.startsWith('> ')) return <motion.blockquote key={i} className="bd-blockquote" variants={fadeUp}>{renderInline(text.slice(2), i)}</motion.blockquote>;

                  // Headings
                  if (text.startsWith('### ')) return <motion.h3 key={i} className="bd-heading bd-heading--3" variants={fadeUp}>{renderInline(text.slice(4), i)}</motion.h3>;
                  if (text.startsWith('## '))  return <motion.h2 key={i} className="bd-heading bd-heading--2" variants={fadeUp}>{renderInline(text.slice(3), i)}</motion.h2>;

                  // Default paragraph — inline rendering handles links, code, bold
                  return <motion.p key={i} className="bd-para" variants={fadeUp}>{renderInline(text, i)}</motion.p>;
                })}
              </div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <motion.div className="bd-tags" variants={fadeUp}>
                  {post.tags.map(function(tag) { return <span key={tag} className="bd-tag">{tag}</span>; })}
                </motion.div>
              )}

              {/* Like */}
              <motion.div className="bd-like" variants={fadeUp}>
                <button
                  className={'bd-like-btn' + (liked ? ' bd-like-btn--liked' : '')}
                  onClick={handleLike} disabled={liked} aria-label="Like this article">
                  <FontAwesomeIcon icon={faHeart} />
                  <span>{liked ? 'Liked!' : 'Like this article'}</span>
                  <span className="bd-like-count">{likes}</span>
                </button>
              </motion.div>

              {/* Share */}
              <motion.div variants={fadeUp}>
                <SharePanel url={shareUrl} copyUrl={copyUrl} title={post.title} excerpt={post.excerpt} />
              </motion.div>

              {/* Comments */}
              <CommentsList postId={post.id} refresh={refresh} />
              <CommentForm postId={post.id} onSubmitted={function() { setRefresh(function(r) { return r + 1; }); }} />

              <motion.div className="bd-nav" variants={fadeUp}>
                <Link to="/blog" className="home-btn home-btn--outline">
                  <FontAwesomeIcon icon={faArrowLeft} /> All Articles
                </Link>
              </motion.div>
            </motion.article>

            {/* Sidebar */}
            <aside className="bd-sidebar" aria-label="More articles">
              {related.length > 0 && (
                <div className="bd-sidebar__card">
                  <p className="bd-sidebar__heading">Related Articles</p>
                  <div className="bd-sidebar__list">
                    {related.map(function(p) {
                      return (
                        <Link key={p.id} to={'/blog/' + p.slug} className="bd-sidebar__item">
                          {p.category && <span className="bd-sidebar__item-category" style={{ color: p.category.color }}>{p.category.name}</span>}
                          <span className="bd-sidebar__item-title">{p.title}</span>
                          <span className="bd-sidebar__item-time"><FontAwesomeIcon icon={faClock} /> {p.readTimeMinutes} min read</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="bd-sidebar__card">
                <p className="bd-sidebar__heading">About the Author</p>
                <div className="bd-author-mini">
                  <div className="bd-author-avatar bd-author-avatar--lg" aria-hidden="true">JCE</div>
                  <p className="bd-author-bio">
                    Jacob C. Eugene is a results-driven software engineer with a passion for building clean, scalable solutions that solve real-world problems.
                  </p>
                  <Link to="/about" className="home-btn home-btn--outline" style={{ width: '100%', justifyContent: 'center' }}>
                    Learn More
                  </Link>
                </div>
              </div>
            </aside>

          </div>
        </Container>
      </section>
    </motion.div>
  );
};

export default BlogDetail;